/**
 * @fileoverview LoadingSkeleton - Reusable skeleton loading component.
 *
 * Provides skeleton screens that show the layout of content while it's loading,
 * improving perceived performance and user experience compared to spinners.
 *
 * @description Supports multiple variants:
 * - 'card': Rectangular card skeleton (for category cards, balance cards)
 * - 'list-item': List item skeleton (for transaction rows)
 * - 'text': Single line text skeleton
 *
 * @module components/LoadingSkeleton
 */
import styles from './LoadingSkeleton.module.css';

/**
 * LoadingSkeleton Component
 *
 * @param {Object} props - Component props
 * @param {string} props.variant - Skeleton variant: 'card', 'list-item', 'text'
 * @param {number} props.count - Number of skeleton items to render (default: 1)
 * @param {string} props.height - Custom height (optional)
 * @param {string} props.width - Custom width (optional)
 */
function LoadingSkeleton({ variant = 'card', count = 1, height, width }) {
    const items = Array.from({ length: count }, (_, i) => i);

    const getSkeletonClass = () => {
        switch (variant) {
            case 'card':
                return styles.skeletonCard;
            case 'list-item':
                return styles.skeletonListItem;
            case 'text':
                return styles.skeletonText;
            default:
                return styles.skeletonCard;
        }
    };

    const customStyle = {};
    if (height) customStyle.height = height;
    if (width) customStyle.width = width;

    return (
        <div className={styles.container}>
            {items.map((i) => (
                <div
                    key={i}
                    className={`${styles.skeleton} ${getSkeletonClass()}`}
                    style={customStyle}
                >
                    <div className={styles.shimmer}></div>
                </div>
            ))}
        </div>
    );
}

export default LoadingSkeleton;
